import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOMeta from '@/components/feature/SEOMeta';
import Breadcrumb from '@/components/base/Breadcrumb';
import VideoCard from '@/components/base/VideoCard';
import { videoCategories, allVideos } from '@/mocks/videos';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'dianying');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeCategory]);

  const currentCategory = videoCategories.find((c) => c.slug === activeCategory);
  const filteredVideos = allVideos.filter((v) => v.category === activeCategory);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory?.name || ''} - 蘑菇视频`,
    description: currentCategory?.description || '',
    url: `https://www.mogu-video.com/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '蘑菇视频',
      url: 'https://www.mogu-video.com',
    },
  };

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <div className="pt-20 pb-20 text-center">
          <h1 className="text-2xl font-bold text-foreground-900">分类未找到</h1>
          <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOMeta
        title={`${currentCategory.name} - 蘑菇视频在线观看`}
        description={`蘑菇视频${currentCategory.name}频道，${currentCategory.description}。海量${currentCategory.name}内容，高清画质流畅播放，尽在蘑菇视频网站。`}
        keywords={`蘑菇视频${currentCategory.name},${currentCategory.name}在线观看,${currentCategory.name}免费播放,蘑菇视频网站`}
        canonicalUrl={`/category/${activeCategory}`}
        ogImage="https://readdy.ai/api/search-image?query=Chinese%20video%20streaming%20category%20page%20with%20warm%20amber%20gradient%2C%20modern%20clean%20layout%2C%20cinematic%20atmosphere&width=1200&height=630&seq=category-og-01&orientation=landscape"
        schema={schema}
      />

      <Navbar />

      <main className="pt-20 pb-16">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory.name },
            ]}
          />

          <div className="mt-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                <i className={`${currentCategory.icon} text-lg`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                  {currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-0.5">
                  {currentCategory.description} · 共 {filteredVideos.length} 部
                </p>
              </div>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="mt-8 flex flex-wrap gap-2">
            {videoCategories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 text-sm rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-white font-medium shadow-sm'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Video Grid */}
          <div className="mt-10" data-product-shop="">
            {filteredVideos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {filteredVideos.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-200 mx-auto">
                  <i className="ri-video-line text-2xl text-foreground-400"></i>
                </div>
                <p className="mt-4 text-foreground-500">暂无相关内容</p>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}